import { daylightFillBalance, warmAtmosphericSun, coolAtmosphericFill, daylightGroundBounce, weatherLightBalance } from "../visual/environmentLighting";
import { landscapePalette } from './landscapePalette';
/**
 * A star system scene: sun light + glow sprite, background starfield, and
 * LOD planets. All positions are kept in system space (float64 JS numbers)
 * and converted to render space each frame by subtracting the floating
 * origin — float32 only ever sees small numbers.
 */
import * as THREE from "three";
import {
  generateSystem, rngFromString, systemPoses, WORLD_SCALE,
  systemDefFor, parseSystemRef, hashSeed,
  systemIdAt, sealedSystemStub,
  type SystemDef, type StarDef, type PaletteDef,
} from "@hyperlight/shared";
import { Planet, sunsetStrength, SUNSET_WARM } from "./planet";
import { TerrainWorkerPool } from "./workerPool";
import type { AtmosphericLight } from "../fx/volumetricAtmosphere";

/** A nearby star system rendered as a real star on the sky. */
export interface NeighborStar {
  /** Lattice id in the legacy core; sealed:<x>:<y>:<z> in the v2 frontier. */
  id: string;
  sx: number;
  sy: number;
  sz: number;
  name: string;
  /** Lattice distance (light-years, fictionally). */
  dist: number;
  /** Unit direction from this system. */
  dir: [number, number, number];
}

/** Apparent-size² → moonlight weight: a moon ~7° in apparent radius (size≈0.12) reads as a
 *  full contribution; smaller/farther moons fall off as the square of their apparent size. */
const MOON_FLUX_K = 70;
/** Peak directional moonlight (a full moon, high in the sky, at deep night). */
const MOON_LIGHT_MAX = 0.55;
/** The night fill colour shifts from the dark-blue new-moon dark side toward this silver
 *  as the lit moon climbs — moonlight is sunlight, only dimmer + perceptually cooler. */
const MOON_FULL_TINT = new THREE.Color(0x9fb0d4);

/** GLSL-style smoothstep on the CPU (0 below e0, 1 above e1, Hermite-eased between). */
function sstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/**
 * Fraction of a disc of angular radius `R` (the sun) covered by a disc of angular radius `r`
 * (an occluding body) whose centre is `d` radians away — the eclipse depth. 0 = clear, 1 =
 * the sun is fully hidden. Standard two-circle lens-overlap area ÷ the sun's area.
 */
function sunCoverage(R: number, r: number, d: number): number {
  if (d >= R + r) return 0; // discs disjoint
  if (d <= Math.abs(R - r)) return r >= R ? 1 : (r * r) / (R * R); // one inside the other
  const d2 = d * d;
  const a1 = Math.acos(Math.max(-1, Math.min(1, (d2 + R * R - r * r) / (2 * d * R))));
  const a2 = Math.acos(Math.max(-1, Math.min(1, (d2 + r * r - R * R) / (2 * d * r))));
  const tri = 0.5 * Math.sqrt(Math.max(0, (-d + r + R) * (d + r - R) * (d - r + R) * (d + r + R)));
  const lens = R * R * a1 + r * r * a2 - tri; // overlap area
  return Math.min(1, lens / (Math.PI * R * R));
}

export class StarSystem {
  readonly def: SystemDef;
  readonly planets: Planet[] = [];
  readonly sunLight: THREE.DirectionalLight;
  /**
   * 1 = local noon, 0 = deep night at the focus position. Computed from the
   * nearest planet's spin each frame; main dims the sky/fog with it so
   * night sides go dark and the stars come out.
   */
  dayFactor = 1;
  /** Golden-hour strength 0..1 at the focus — peaks at the terminator, 0 at noon/night
   *  and only over planets with an atmosphere. Warms the sky/haze/lighting. */
  sunsetK = 0;
  /** Interiors P1: eased 0..1 while the player stands inside a building shell
   *  (set by main each frame). Dims the global ambient/hemi sky fill, which
   *  otherwise shines straight through walls (no global illumination). */
  interiorDim = 0;
  private ambient: THREE.AmbientLight;
  private hemi: THREE.HemisphereLight;
  private readonly starColorBase = new THREE.Color();
  private readonly sunsetLight = new THREE.Color(SUNSET_WARM[0], SUNSET_WARM[1], SUNSET_WARM[2]);
  /** Cool moonlight/starlight the fill takes on the night side + in open space. */
  // Night fill colour: cool but LUMINOUS — it multiplies the ambient/hemi
  // floor, so a too-dark slate here makes moonless nights unreadably black.
  private readonly moonLight = new THREE.Color(0x7d90b8);
  private readonly skyFillTmp = new THREE.Color();
  private readonly moonTint = new THREE.Color();
  private groundPalette:PaletteDef|null = null;
  /** Summed phase-weighted moonlight at the focus: 0 (new moon / no moon up) … 1 (bright full
   *  moon, high). Drives the night ambient floor + the moon fill light's intensity/colour. */
  moonGlow = 0;
  /** SOLAR eclipse depth at the focus: 0 = sun clear, 1 = a moon (or body) totally hides it.
   *  Dims the sun + sky; main.ts reads it to darken the sky toward twilight during totality. */
  eclipse = 0;
  private readonly _eVec = new THREE.Vector3();
  private readonly _eSun = new THREE.Vector3();
  private readonly _lVec = new THREE.Vector3();
  /** Cool fill light from the brightest lit moon above the horizon. NO shadow — the sun owns
   *  the single shadow map; this just gives the night some directional shape that tracks the
   *  moon across the sky. */
  private readonly moonDirLight = new THREE.DirectionalLight(0x9fb0d4, 0);
  /**
   * Every DIRECTIONAL light this system contributes — the sun, and the moon fill
   * that carries the night. The painted light model (world/stylizedShading.ts)
   * needs the total direct irradiance to normalise its band coordinate, and
   * asking the lights themselves is the only version of that answer which cannot
   * fall out of step with what three actually rendered.
   */
  readonly directionalLights: readonly THREE.DirectionalLight[];
  readonly atmosphereLights: AtmosphericLight[] = Array.from({ length: 3 }, () => ({ direction: new THREE.Vector3(), color: new THREE.Color(0, 0, 0) }));
  private readonly moonSources = new Map<string, { direction: THREE.Vector3; flux: number }>();
  private selectedMoon = "";
  private readonly starlightTan = new THREE.Vector3();
  private readonly moonDir = new THREE.Vector3(0, 1, 0);
  private readonly _mFocus = new THREE.Vector3();
  private readonly _mUp = new THREE.Vector3();
  private readonly _mTo = new THREE.Vector3();
  private sunGlow: THREE.Sprite;
  private starBody: THREE.Group;
  private starMat: THREE.ShaderMaterial;
  private flares: FlareRec[] = [];
  private starfield: THREE.Points;
  private celestials: THREE.Group;
  private beltGroup: THREE.Group;
  private neighborGroup: THREE.Group;
  /**
   * Every system within 3 lattice units, each drawn as a bright star at its
   * TRUE direction on the sky — the chart's coordinates, made visible.
   * main.ts matches the ship's nose against these for point-and-jump.
   */
  readonly neighbors: NeighborStar[] = [];
  private aimedIdx: number | null = null;
  /** Eased highlight strengths per neighbor index (fade in/out, no pops). */
  private aimGlow = new Map<number, { k: number; target: number }>();
  /** Directions (unit) to the first few nebulae — parallax debug/testing. */
  readonly celestialDirs: [number, number, number][] = [];
  private pool: TerrainWorkerPool;
  private lodTimer = 0;
  /** Everything we added to the scene, for clean teardown on system switch. */
  private sceneExtras: THREE.Object3D[] = [];

  /** Lattice coords, or null for a home:<uid> system (C4) — every piece of
   *  coordinate ARITHMETIC (neighbor stars, jump reach, chart plotting) guards
   *  on this; identity-only consumers use def.id. */
  readonly coords: [number, number, number] | null;

  constructor(
    readonly scene: THREE.Scene,
    id: string,
  ) {
    const def = systemDefFor(id);
    if (!def) throw new Error(`StarSystem: unresolvable system id "${id}"`);
    this.def = def;
    const ref = parseSystemRef(id);
    this.coords = ref !== null && (ref.kind === "lattice" || ref.kind === "sealed") ? ref.c : null;
    // Home systems have no lattice point; celestial parallax (visual only)
    // takes a stable hash-derived vantage so each home gets its own sky.
    const vantage: [number, number, number] = this.coords ?? [
      (hashSeed(`${id}:vx`) % 2000) / 100 - 10,
      (hashSeed(`${id}:vy`) % 2000) / 100 - 10,
      (hashSeed(`${id}:vz`) % 2000) / 100 - 10,
    ];
    const gx = vantage[0], gy = vantage[1], gz = vantage[2];
    this.pool = new TerrainWorkerPool();

    for (const p of this.def.planets) {
      const planet = new Planet(p, this.pool);
      this.planets.push(planet);
      scene.add(planet.group);
    }

    const starColor = new THREE.Color(this.def.star.color);
    this.starColorBase.copy(starColor); // base hue the sun light warms FROM at sunset
    this.sunLight = new THREE.DirectionalLight(starColor, this.def.star.intensity);
    this.directionalLights = [this.sunLight, this.moonDirLight];
    this.atmosphereLights[0].shadow = this.sunLight;
    this.atmosphereLights[1].shadow = this.moonDirLight;
    // Real-time shadows (single player-centred map). The light sits ~10 km sunward of the
    // focus, so the ortho shadow camera's near/far BRACKET that distance; coverage reaches
    // the items you walk among (~200 m). Bias stays tiny — the char/ground depth gap is a
    // small fraction of the slab, so a big bias washes the shadow out (peter-panning).
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(4096, 4096);
    {
      const sc = this.sunLight.shadow.camera;
      sc.near = 9650;
      sc.far = 10350;
      sc.left = sc.bottom = -200;
      sc.right = sc.top = 200;
      sc.updateProjectionMatrix();
    }
    this.sunLight.shadow.bias = -0.0001;
    this.sunLight.shadow.normalBias = 0.15;
    // NIGHT shadows: the moon/starlight fill casts its own (cheaper, softer)
    // map — moonlit nights get real shadows, and a faint starlight floor keeps
    // small shadows under trees/props even with no moon up. Same bracket
    // geometry as the sun (light sits 8 km out along its direction).
    this.moonDirLight.castShadow = true;
    this.moonDirLight.shadow.mapSize.set(2048, 2048);
    {
      const mc = this.moonDirLight.shadow.camera;
      mc.near = 7650;
      mc.far = 8350;
      mc.left = mc.bottom = -140;
      mc.right = mc.top = 140;
      mc.updateProjectionMatrix();
    }
    this.moonDirLight.shadow.bias = -0.0001;
    this.moonDirLight.shadow.normalBias = 0.2;
    // Generous fill so night sides stay navigable (no global illumination yet).
    this.ambient = new THREE.AmbientLight(0x445566, 0.85);
    this.hemi = new THREE.HemisphereLight(0x99aacc, 0x333344, 0.45);
    this.sunGlow = makeSunSprite(starColor);
    const sb = makeStarBody(this.def.star);
    this.starBody = sb.group;
    this.starMat = sb.mat;
    this.flares = sb.flares;
    // The backdrop is the same distant universe from every galaxy — a fixed
    // seed keeps the stars (and any in-flight streaking) continuous across
    // hyperlight arrivals instead of popping to a new pattern.
    this.starfield = makeStarfield("hyperlight:backdrop");
    this.starStreaks = makeStarStreaks(this.starfield);
    this.tickPoses(Date.now() / 1000);
    this.tickPoses(Date.now() / 1000); // twice: posePrev = pose (no ghost delta)
    // Nebulae and the galactic band are anchored in UNIVERSE space and
    // viewed from this galaxy's coordinate — neighboring galaxies see the
    // same features from measurably different angles.
    this.celestials = makeCelestials(gx, gy, gz, this.celestialDirs);
    // Asteroid belts: seeded annuli of drifting rock between the orbits.
    this.beltGroup = makeBelts(this.def);
    // Neighboring systems as real, aimable stars — lattice systems only. A
    // home system (C4) has no lattice neighborhood: the sky shows no jump
    // stars and `neighbors` stays empty (aim-to-jump naturally disabled).
    this.neighborGroup = this.coords !== null
      ? makeNeighborStars(this.coords[0], this.coords[1], this.coords[2], this.neighbors)
      : new THREE.Group();
    this.sceneExtras = [
      this.beltGroup,
      this.starBody,
      this.neighborGroup,
      this.sunLight,
      this.sunLight.target,
      this.moonDirLight,
      this.moonDirLight.target,
      this.ambient,
      this.hemi,
      this.sunGlow,
      this.starfield,
      this.starStreaks,
      this.celestials,
    ];
    for (const o of this.sceneExtras) scene.add(o);
  }

  private starStreaks: THREE.LineSegments;

  /** Terrain builds still in flight — the warp tunnel reads this to hold
   *  its dissolve until the destination has ground to stand on. */
  get meshing(): number {
    return this.pool.queueDepth;
  }

  private idleFrames = 0;

  /** True once meshing has been quiet for a sustained window — LOD splits
   *  come in waves, so a momentarily empty queue is NOT "loaded". */
  get arrivalReady(): boolean {
    // 90 consecutive idle frames: LOD split waves pause ~15 frames between
    // levels (the 0.12 s walk cadence), which a shorter window mistook for
    // "loaded" — the tunnel then dissolved over a half-built planet.
    return this.idleFrames > 90;
  }

  /** Restart the sustained-idle window. The load screen calls this when it
   *  opens: `arrivalReady`/`idleFrames` otherwise carry the title-screen orbit's
   *  long-settled count, so the loader would read "ready" before the spawn's
   *  meshing wave even starts (unlike a hyperlight arrival, which gets a fresh
   *  StarSystem with idleFrames = 0). */
  resetTerrainIdle(): void {
    this.idleFrames = 0;
  }

  private tmpB = new THREE.Vector3();
  private _shadowFocus = new THREE.Vector3();
  private _shadowLocal = new THREE.Vector3();
  private tmpC = new THREE.Vector3();

  /**
   * Highlight the neighbor star the nose is resting on (by index into
   * `neighbors`), or clear with null. The aimed star swells and pulses.
   */
  setAimedNeighbor(idx: number | null): void {
    if (idx === this.aimedIdx) return;
    if (this.aimedIdx !== null) {
      const g = this.aimGlow.get(this.aimedIdx);
      if (g) g.target = 0; // fade out from wherever it is
    }
    this.aimedIdx = idx;
    if (idx !== null) {
      const sp = this.neighborGroup.children[idx] as THREE.Sprite;
      sp.userData.baseOpacity ??= sp.material.opacity;
      const g = this.aimGlow.get(idx);
      if (g) g.target = 1;
      else this.aimGlow.set(idx, { k: 0, target: 1 });
    }
  }

  /**
   * Day factor (0 night → 1 noon) for the side of a planet facing
   * `viewPos`. PHYSICAL now: the star lights one hemisphere; the planet's
   * rotation (carrying you with it) is what changes your local time.
   */
  private planetDay(p: Planet, viewPos: THREE.Vector3): number {
    const sunDir = this.tmpC.copy(p.pose.center).normalize(); // star → planet
    const up = this.tmpB.copy(viewPos).sub(p.pose.center).normalize();
    const noon = -up.dot(sunDir);
    const t = Math.max(0, Math.min(1, (noon + 0.25) / 0.5));
    return t * t * (3 - 2 * t);
  }

  /**
   * Hide the system's bodies (planets, sun glow) — used while riding the
   * warp tunnel, which doubles as a loading screen.
   */
  setBodiesVisible(visible: boolean): void {
    for (const p of this.planets) p.group.visible = visible;
    this.sunGlow.visible = visible;
    // The PHYSICAL sun and the belts too — a tunnel that hides planets but
    // leaves the destination star blazing through its walls isn't hiding
    // anything.
    this.starBody.visible = visible;
    this.beltGroup.visible = visible;
  }

  /** Toggle the ENTIRE backdrop — starfield, streaks, nebula/celestial paints and
   *  neighbor stars. The outfit void wants a featureless black screen, so it hides
   *  everything `setBodiesVisible` leaves up. While hidden, `setStarStreak` (called
   *  every frame) is a no-op so it can't re-show the stars. */
  setStarfieldVisible(visible: boolean): void {
    this.backdropHidden = !visible;
    this.starfield.visible = visible;
    this.starStreaks.visible = false;
    this.celestials.visible = visible;
    this.neighborGroup.visible = visible;
  }
  private backdropHidden = false;

  /** DEBUG: toggle sky-layer visibility across all planets to isolate horizon artefacts. */
  setSkyDebug(opts: { atmo?: boolean; sea?: boolean }): void {
    for (const p of this.planets) {
      if (opts.atmo !== undefined && p.atmo) p.atmo.visible = opts.atmo;
      if (opts.sea !== undefined && p.sea) p.sea.visible = opts.sea;
    }
  }

  /** Feed the scene fog colour to every atmosphere shell so it can converge to it at
   *  the horizon (seamless sky/ground meeting — no bright "gap" band). */
  setAtmoFog(col: THREE.Color): void {
    for (const p of this.planets) {
      const u = (p.atmo?.material as THREE.ShaderMaterial | undefined)?.uniforms?.uFog;
      if (u) (u.value as THREE.Vector3).set(col.r, col.g, col.b);
    }
  }

  /** DEBUG: set the atmosphere visualization mode on every shell (0 off, 1 coverage,
   *  2 elevation, 3 optical depth, 4 alpha). */
  setAtmoDebug(mode: number): void {
    for (const p of this.planets) {
      const u = (p.atmo?.material as THREE.ShaderMaterial | undefined)?.uniforms?.uDebug;
      if (u) u.value = mode;
    }
  }

  /**
   * Red-shift effect for hyperlight: the EXISTING background stars stretch
   * into lines along the travel axis and FLOW toward the camera, wrapping
   * around the shell. amount 0 restores normal points; dir is the (unit,
   * world-space) travel direction.
   */
  setStarStreak(amount: number, dir: THREE.Vector3, dt: number): void {
    // The void editors hide the whole backdrop — don't let the per-frame streak
    // logic flip the stars back on.
    if (this.backdropHidden) return;
    const pointsMat = this.starfield.material as THREE.PointsMaterial;
    const lineMat = this.starStreaks.material as THREE.LineBasicMaterial;
    // The painted sky (nebulae, band, neighbor stars) smears away early in
    // the charge and reappears at arrival with the DESTINATION's geometry —
    // celestial bodies ride the jump like everything else.
    const paintFade = Math.max(0, 1 - amount * 2.4);
    for (const g of [this.celestials, this.neighborGroup]) {
      g.visible = paintFade > 0.01;
      if (g.visible) {
        g.traverse((o) => {
          if (o instanceof THREE.Sprite || o instanceof THREE.Points) {
            const m = (o as THREE.Sprite).material as THREE.Material & { opacity: number };
            const base = (o.userData.baseOpacity ??= m.opacity) as number;
            m.opacity = base * paintFade;
          }
        });
      }
    }
    if (amount <= 0.005) {
      this.starStreaks.visible = false;
      this.starfield.visible = true;
      pointsMat.opacity = 1;
      return;
    }
    // The points morph into the lines via CROSSFADE — binary swapping made
    // the sky blink out at the start and pop back at the end.
    this.starStreaks.visible = true;
    lineMat.opacity = Math.min(1, amount / 0.08);
    this.starfield.visible = amount < 0.45;
    pointsMat.opacity = Math.max(0, 1 - amount / 0.35);

    // Streaks SWEEP; stars stay put. Each star keeps its true sky position
    // and its line slides through it along the travel axis over a short
    // local span, fading at the span ends and recycling. Collectively the
    // sky flows toward the viewer, but nothing is ever repositioned — so
    // there is no "ring" (no redistribution) and no scrambling (no star
    // strays beyond its own streak's neighborhood).
    streakPhase += dt * (0.25 + 0.95 * amount);
    const base = this.starfield.geometry.getAttribute("position").array as Float32Array;
    const baseCol = this.starfield.geometry.getAttribute("color").array as Float32Array;
    const out = this.starStreaks.geometry.getAttribute("position") as THREE.BufferAttribute;
    const colOut = this.starStreaks.geometry.getAttribute("color") as THREE.BufferAttribute;
    const arr = out.array as Float32Array;
    const cols = colOut.array as Float32Array;
    const ud = this.starStreaks.userData;
    const jitter = ud.lenJitter as Float32Array;
    const phase = ud.sweepPhase as Float32Array;
    const rate = ud.sweepRate as Float32Array;
    // Gentler curve than amount² so the skew is visible early in the charge.
    const len = Math.pow(amount, 1.6) * 240_000;

    const holeK = Math.min(1, amount * 1.4);
    for (let i = 0; i < base.length; i += 3) {
      const s = i / 3;
      // Clear vista dead ahead: streaks within ~7° of the travel axis sit
      // inside the tunnel's central circle — fade them to nothing (eased to
      // full presence by ~14°) instead of capping the tube with geometry.
      const bl = Math.hypot(base[i], base[i + 1], base[i + 2]) || 1;
      const ad = (base[i] * dir.x + base[i + 1] * dir.y + base[i + 2] * dir.z) / bl;
      const ax = Math.max(0, Math.min(1, (ad - 0.97) / 0.022));
      const hole = 1 - ax * ax * (3 - 2 * ax) * holeK;
      const L = len * jitter[s];
      // Sweep cycle: u in [0,1); the line's center slides from ahead of the
      // star to behind it (toward the viewer), then fades and recycles.
      const u = (streakPhase * rate[s] + phase[s]) % 1;
      const o2 = (0.5 - u) * L * 1.3;
      const x = base[i] + dir.x * o2;
      const y = base[i + 1] + dir.y * o2;
      const z = base[i + 2] + dir.z * o2;
      const o = i * 2;
      arr[o] = x + dir.x * L * 0.2;
      arr[o + 1] = y + dir.y * L * 0.2;
      arr[o + 2] = z + dir.z * L * 0.2;
      arr[o + 3] = x - dir.x * L * 0.8;
      arr[o + 4] = y - dir.y * L * 0.8;
      arr[o + 5] = z - dir.z * L * 0.8;
      // Fade at the ends of the sweep hides the recycle completely.
      const fade = Math.sin(u * Math.PI) * hole;
      cols[o] = baseCol[i] * fade;
      cols[o + 1] = baseCol[i + 1] * fade;
      cols[o + 2] = baseCol[i + 2] * fade;
      cols[o + 3] = baseCol[i] * 0.1 * fade;
      cols[o + 4] = baseCol[i + 1] * 0.1 * fade;
      cols[o + 5] = baseCol[i + 2] * 0.1 * fade;
    }
    out.needsUpdate = true;
    colOut.needsUpdate = true;
  }

  private bodyZoom = 1;

  /**
   * Arrival warp-zoom: planets (and the sun glow) scale in from a point as
   * the tunnel dissolves, so the destination rushes into view.
   */
  setArrivalZoom(s: number): void {
    const k = Math.max(0.001, Math.min(1, s));
    this.bodyZoom = k;
    for (const p of this.planets) p.group.scale.setScalar(k);
  }

  private departDir: THREE.Vector3 | null = null;
  private departK = 0;
  private departQuat = new THREE.Quaternion();

  /**
   * Departure "zip past": as the ship goes superluminal, planets sweep
   * BACKWARD along the travel axis with quadratic acceleration while
   * elongating along it — the light-speed smear — instead of deflating.
   * Pass (null, 0) to reset. Applied per-frame in update().
   */
  setDepartureWarp(dir: THREE.Vector3 | null, k: number): void {
    this.departDir = dir;
    this.departK = Math.max(0, Math.min(1, k));
    if (dir) {
      const m = new THREE.Matrix4().lookAt(
        new THREE.Vector3(),
        dir,
        new THREE.Vector3(0, 1, 0),
      );
      this.departQuat.setFromRotationMatrix(m); // local Z axis ∥ travel axis
    }
  }

  /**
   * Refresh every body's pose at simulation time `t` (seconds). The CALLER
   * owns the clock: main ticks this in lockstep with fixed sim steps, so
   * the ground and everyone standing on it move as one — mixed clocks were
   * pure jitter. posePrev keeps the previous tick for exact carry deltas.
   */
  tickPoses(t: number): void {
    const rawPoses = systemPoses(this.def, t);
    for (let i = 0; i < this.planets.length; i++) {
      const p = this.planets[i];
      const rp = rawPoses[i];
      p.posePrev.center.copy(p.pose.center);
      p.posePrev.spinAngle = p.pose.spinAngle;
      p.pose.center.set(rp.center[0], rp.center[1], rp.center[2]);
      p.pose.spinAngle = rp.spinAngle;
      this.tmpB.set(p.def.spin.axis[0], p.def.spin.axis[1], p.def.spin.axis[2]);
      p.pose.quat.setFromAxisAngle(this.tmpB, rp.spinAngle);
      p.pose.invQuat.copy(p.pose.quat).invert();
    }
  }

  /** Planet whose surface is closest to the given system-space position.
   *  Uses LIVE pose centers — bodies orbit now. */
  nearestPlanet(pos: THREE.Vector3): { planet: Planet; surfaceDist: number } | null {
    let best: Planet | null = null;
    let bestDist = Infinity;
    for (const p of this.planets) {
      const d = pos.distanceTo(p.pose.center) - p.def.radius;
      if (d < bestDist) {
        bestDist = d;
        best = p;
      }
    }
    return best ? { planet: best, surfaceDist: bestDist } : null;
  }

  /** Body (planet OR moon) by its stable def id, or null if not in this system.
   *  Used to re-anchor a saved on-foot position to the exact body it was on —
   *  bodies orbit + spin on the wall clock, so "nearest to the old point" drifts
   *  onto moons or into space after any real time away. */
  planetById(id: string): Planet | null {
    return this.planets.find((p) => p.def.id === id) ?? null;
  }

  /** True when the planet under `focus` has fully meshed its visible area AND
   *  meshing has been quiet for a sustained window — the loader's "immediate
   *  area is ready" signal. Uses `arrivalReady`, NOT a momentary empty queue:
   *  LOD splits (and the scatter/trees they carry) arrive in waves, so a queue
   *  that is empty for one frame is not "loaded" — it closes the loader over a
   *  half-built planet with the deep scatter patches still pending. */
  terrainSettled(focus: THREE.Vector3): boolean {
    const near = this.nearestPlanet(focus);
    return !!near && near.planet.covered && this.arrivalReady;
  }

  /**
   * Per-frame: reposition everything relative to the floating origin and
   * (throttled) refresh planet LOD trees. `shipPos` is the player focus
   * (ship in flight, character on foot); `onFoot` unlocks the extra
   * near-ground LOD levels.
   */
  update(
    dt: number,
    shipPos: THREE.Vector3,
    origin: THREE.Vector3,
    camPos: THREE.Vector3,
    onFoot = false,
    alpha = 1,
  ): void {
    // Poses are ticked by the SIM loop (lockstep with the carry); here we
    // only consume them.

    // Viewer's local air density (for sky-relative effects below).
    const nearP = this.nearestPlanet(shipPos);
    this.groundPalette = nearP ? landscapePalette(nearP.planet.def) : null;
    const atmoH = nearP?.planet.def.atmosphere?.height ?? 0;
    const viewerRho =
      nearP && atmoH > 0 ? Math.max(0, 1 - Math.max(0, nearP.surfaceDist) / (atmoH * 3)) : 0;

    for (const p of this.planets) {
      // RENDER pose interpolates between the last two sim ticks (same
      // alpha as the ship/character) — bodies stepping once per sim tick
      // while everything else lerps reads as ground/hover jitter at high
      // refresh rates. Sim queries keep using p.pose untouched.
      p.group.position.lerpVectors(p.posePrev.center, p.pose.center, alpha).sub(origin);
      if (this.departDir) {
        const slide = this.departK * this.departK * 420_000;
        p.group.position.addScaledVector(this.departDir, -slide);
        p.group.quaternion.copy(this.departQuat);
        const stretch = 1 + this.departK * 6;
        p.group.scale.set(this.bodyZoom, this.bodyZoom, this.bodyZoom * stretch);
      } else {
        // Shortest-angle interp: spinAngle wraps 2π→0 once per spin period, and a
        // naive lerp would spin the whole group (ground AND clouds) backward a full
        // turn over that one tick — a visible "snap back" every period. Fold the
        // delta into [-π, π] so the wrap tick advances by its true small step.
        let dAng = p.pose.spinAngle - p.posePrev.spinAngle;
        dAng -= Math.round(dAng / (Math.PI * 2)) * (Math.PI * 2);
        const ang = p.posePrev.spinAngle + dAng * alpha;
        this.tmpB.set(p.def.spin.axis[0], p.def.spin.axis[1], p.def.spin.axis[2]);
        p.group.quaternion.setFromAxisAngle(this.tmpB, ang); // the ground turns
      }
      // Inside one planet's air, OTHER shells must dim with the haze —
      // unfogged shader glows otherwise ring every planet on the horizon.
      const fade = p === nearP?.planet ? 1 : 1 - viewerRho * 0.95;
      const dp = this.planetDay(p, shipPos);
      // Lunar eclipse: a MOON sliding into its parent planet's umbra (the parent occludes the
      // sun as seen from the moon) dims + reddens toward a blood-moon copper. Deterministic
      // geometry, same as the solar case but with the planet as the occluder.
      let lunarShadow = 0;
      if (p.def.parentId) {
        const parent = this.planets.find((q) => q.def.id === p.def.parentId);
        if (parent) {
          const mr = p.pose.center.length() || 1;
          const pv = this._lVec.copy(parent.pose.center).sub(p.pose.center);
          const pd = pv.length() || 1;
          const sep = Math.acos(Math.max(-1, Math.min(1, -pv.dot(p.pose.center) / (pd * mr))));
          lunarShadow = sunCoverage(this.def.star.radius / mr, parent.def.radius / pd, sep);
        }
      }
      // Golden-hour shell warming is gated by viewerRho too — from space the atmosphere keeps
      // its base colour (no sunset rim); it only reddens once you're actually inside the air.
      p.updateAtmosphereCam(camPos, dp, fade, sunsetStrength(dp) * viewerRho, this.starColorBase, lunarShadow);
    }

    // Star sits at the system origin; light shines from it toward the ship.
    // Near a planet the light direction rotates around the planet's spin
    // axis over wall-clock time: geometry stays put (determinism), the SUN
    // sweeps — same day/night physics as a turning planet, seen from it.
    const dist = shipPos.length() || 1;
    const starward = shipPos.clone().divideScalar(dist); // unit, star -> ship
    const sunDir = starward; // physical: light always radiates from the star
    this.dayFactor = 1;
    if (nearP && nearP.surfaceDist < nearP.planet.def.radius * 2) {
      const k = 1 - Math.max(0, Math.min(1, nearP.surfaceDist / (nearP.planet.def.radius * 2)));
      this.dayFactor = 1 + (this.planetDay(nearP.planet, shipPos) - 1) * k;
    }
    // Shadow focus rides the RENDER pose: near a planet, re-express the sim
    // focus in the planet's INTERPOLATED frame (group = lerped centre +
    // shortest-angle spin, written above) — aiming the shadow slab at the raw
    // sim pose makes every shadow sawtooth against the lerped ground whenever
    // the planet carries the player (the cape/cutscene jitter class).
    const shadowFocus = this._shadowFocus.copy(shipPos).sub(origin);
    if (nearP) {
      const p = nearP.planet;
      this._shadowLocal.copy(shipPos).sub(p.pose.center).applyQuaternion(p.pose.invQuat);
      shadowFocus.copy(this._shadowLocal).applyQuaternion(p.group.quaternion).add(p.group.position);
    }
    this.sunLight.position
      .copy(shadowFocus)
      .addScaledVector(sunDir, -10000); // 10 km sunward of the ship
    this.sunLight.target.position.copy(shadowFocus);
    // HemisphereLight uses its world position as the sky direction. Use the
    // same interpolated planet frame as shadows, not global +Y or an absolute
    // system-space position (which changes under floating-origin shifts).
    if (nearP) this.hemi.position.copy(shadowFocus).sub(nearP.planet.group.position).normalize();
    else this.hemi.position.set(0, 1, 0);

    // --- Solar eclipse: a body (a moon, usually) passing in front of the sun casts its shadow
    // over the player. `eclipse` = the fraction of the sun's disc it hides — it dims the sun
    // (below) + the sky (main.ts) toward an eerie twilight, deepening to near-dark at totality.
    this.eclipse = 0;
    const sunAngR = this.def.star.radius / dist; // sun's apparent radius from here
    const sunFrom = this._eSun.copy(shipPos).divideScalar(-dist); // unit, ship → sun
    for (const p of this.planets) {
      if (nearP && p === nearP.planet) continue; // the body you stand on = night, not an eclipse
      const v = this._eVec.copy(p.pose.center).sub(shipPos);
      const bd = v.length();
      if (bd >= dist || bd < 1) continue; // must sit BETWEEN you and the sun to occlude it
      const sep = Math.acos(Math.max(-1, Math.min(1, v.dot(sunFrom) / bd)));
      const cov = sunCoverage(sunAngR, p.def.radius / bd, sep);
      if (cov > this.eclipse) this.eclipse = cov;
    }

    // Golden hour: as the sun grazes the horizon (the terminator) it DIMS + reddens — but only
    // FROM INSIDE the atmosphere (the reddening + extinction are atmospheric scattering). Scale
    // by viewerRho so it fades out as you climb to space — no golden cast on a ship in orbit.
    this.sunsetK = nearP?.planet.def.atmosphere ? sunsetStrength(this.dayFactor) * viewerRho : 0;
    this.sunLight.intensity =
      this.def.star.intensity *
      (0.04 + 0.96 * this.dayFactor) *
      (1 - this.sunsetK * 0.22) *
      (1 - this.eclipse * 0.97); // the moon's shadow blocks the direct sun
    this.sunLight.color.copy(this.starColorBase).lerp(this.sunsetLight, this.sunsetK * 0.82);
    warmAtmosphericSun(this.sunLight.color, this.dayFactor, nearP?.planet.def.atmosphere ? viewerRho : 0);

    // --- Phase-driven moonlight ------------------------------------------------------------
    // A moon casts light in proportion to its ILLUMINATED FRACTION — the Sun–Moon–Observer
    // geometry, full when it sits opposite the sun, new when between us and the sun — times
    // its apparent size² (a fuller, larger moon throws more light), and only while it's above
    // the local horizon. Summed over every moon → moonGlow, which lifts the night ambient
    // floor and powers a cool no-shadow fill light from the brightest moon's direction.
    let glow = 0;
    let best = 0;
    let bestScore = 0, bestId = "";
    // Player in render space — riding the interpolated planet frame near a
    // body (same anti-sawtooth re-expression as the sun shadow focus above).
    const focus = this._mFocus.copy(this._shadowFocus);
    const upM = nearP ? this._mUp.copy(shipPos).sub(nearP.planet.pose.center).normalize() : null;
    for (const p of this.planets) {
      if (!p.def.parentId) continue; // moons only — close + sky-dominant
      const mc = p.pose.center;
      const to = this._mTo.copy(mc).sub(shipPos); // observer → moon (system space)
      const md = to.length() || 1;
      to.divideScalar(md); // unit
      const illum = 0.5 * (1 + mc.dot(to) / (mc.length() || 1)); // ½(1 + dot(moonCentrê, obs→moon))
      const size = p.def.radius / md; // apparent radius (radians)
      let k = illum * Math.min(1, size * size * MOON_FLUX_K);
      if (upM) k *= sstep(-0.05, 0.18, upM.dot(to)); // fade out as it sets below the horizon
      glow += k;
      let source = this.moonSources.get(p.def.id);
      if (!source) { source = { direction: new THREE.Vector3(), flux: 0 }; this.moonSources.set(p.def.id, source); }
      source.direction.copy(to); source.flux = k;
      const score = k * (p.def.id === this.selectedMoon ? 1.12 : 1);
      if (score > bestScore) {
        best = k;
        bestScore = score; bestId = p.def.id;
        this.moonDir.copy(to); // brightest moon → fill-light direction (system dir = render dir)
      }
    }
    this.moonGlow = Math.min(1, glow);
    this.selectedMoon = bestId;
    const sunSource = this.atmosphereLights[0];
    sunSource.direction.copy(this.sunLight.position).sub(this.sunLight.target.position).normalize();
    sunSource.color.copy(this.sunLight.color).multiplyScalar(this.def.star.intensity * this.dayFactor * (1 - this.sunsetK * 0.22) * (1 - this.eclipse * 0.97));
    const moonSource = this.atmosphereLights[1], secondary = this.atmosphereLights[2];
    moonSource.direction.copy(this.moonDir);
    moonSource.shadow = best >= 0.02 ? this.moonDirLight : undefined;
    moonSource.color.copy(this.moonDirLight.color).multiplyScalar(MOON_LIGHT_MAX * 0.8 * best * (1 - this.dayFactor));
    let second = 0;
    secondary.color.setRGB(0, 0, 0);
    for (const [id, source] of this.moonSources) if (id !== bestId && source.flux > second) {
      second = source.flux;
      secondary.direction.copy(source.direction);
      secondary.color.copy(this.moonDirLight.color).multiplyScalar(MOON_LIGHT_MAX * 0.8 * second * (1 - this.dayFactor));
    }
    // Fill light sits OUT along the brightest moon's direction, aimed back at the player; its
    // intensity tracks the glow and fades by day — with a small STARLIGHT floor so night
    // scenes always keep faint directional shadows (moonless nights would otherwise go flat).
    // With no moon up, light from high overhead (planet-up tilted a little) for short shadows.
    if (best < 0.02 && upM) {
      this.starlightTan.set(0, 1, 0);
      this.starlightTan.crossVectors(upM, this.starlightTan); // three reads operands before writing
      if (this.starlightTan.lengthSq() < 0.01) this.starlightTan.set(1, 0, 0);
      this.starlightTan.normalize();
      this.moonDir.copy(upM).multiplyScalar(0.9).addScaledVector(this.starlightTan, 0.45).normalize();
    }
    this.moonDirLight.position.copy(focus).addScaledVector(this.moonDir, 8000);
    this.moonDirLight.target.position.copy(focus);
    this.moonDirLight.intensity = MOON_LIGHT_MAX * (0.2 + 0.8 * this.moonGlow) * (1 - this.dayFactor);
    // Only render the night shadow map when it can matter (saves a full shadow
    // pass by day, when the sun owns shadows and this light is ~off anyway).
    this.moonDirLight.castShadow = this.dayFactor < 0.55;

    // Sun shadows FADE OUT as the sun grazes the local horizon. At low or
    // below-horizon angles the whole ±200 m shadow box reads "in shadow"
    // (grazing depth precision + km-long real shadows) while ground beyond
    // the box never samples the map and still catches the residual sun —
    // a razor-straight brightness edge at the frustum boundary that tracks
    // the player. Invisible on rough wilderness; a ruler line across the
    // flat, uniform settlement pads. No sun overhead → no sun shadows.
    const sunElev = upM ? -upM.dot(starward) : 1; // sin(elevation) at the player
    this.sunLight.shadow.intensity = sstep(0.02, 0.16, sunElev);

    // Night ambient/hemi floor rises with the moon: a dim-but-readable new-moon
    // night vs a silvery full-moon one (daytime is unchanged — the floor only
    // acts as dayFactor → 0). The base sits high enough that ground, props and
    // paths stay legible on foot even with no moon up.
    const nightFloor = 0.38 + 0.22 * this.moonGlow;
    // The sky's scattered fill also drops under the moon's shadow (but never to full dark —
    // the corona + the uneclipsed sky beyond the umbra still glow), for the eerie eclipse dusk.
    const eclK = 1 - this.eclipse * 0.7;
    // Interiors P1: ambient/hemi are GLOBAL and unshadowed — walls don't block
    // them — so interior darkness is AUTHORED: main eases interiorDim toward 1
    // while the player stands inside a building shell and the sky fill drops to
    // a dim floor (never zero: rooms stay readable pre-P2-lamps).
    const inK = 1 - 0.6 * this.interiorDim;
    const fillBalance = daylightFillBalance(this.dayFactor, viewerRho);
    this.ambient.intensity = fillBalance.ambient * 0.85 * (nightFloor + (1 - nightFloor) * this.dayFactor) * eclK * inK;
    this.hemi.intensity = fillBalance.hemisphere * 0.45 * (nightFloor * 0.85 + (1 - nightFloor * 0.85) * this.dayFactor) * eclK * inK;
    // ambient + hemi COLOURS are sky-driven — set by applySkyLighting() once main has
    // computed the sky colour (blue noon → warm golden hour → cool moonlit night).
    this.sunGlow.position.set(-origin.x, -origin.y, -origin.z);
    if (this.departDir) {
      this.sunGlow.position.addScaledVector(this.departDir, -this.departK * this.departK * 420_000);
    }
    // The star's surface boils; prominences SPURT off it and settle back.
    // During warp departure it ZIPS PAST with everything else.
    this.starBody.position.copy(this.sunGlow.position);
    if (this.departDir) {
      const slide = this.departK * this.departK * 420_000;
      this.starBody.position.addScaledVector(this.departDir, -slide);
      const shrink = Math.max(0.001, 1 - this.departK * 1.1);
      this.starBody.scale.setScalar(shrink * this.bodyZoom);
    } else {
      this.starBody.scale.setScalar(this.bodyZoom);
    }
    this.beltGroup.position.copy(this.sunGlow.position);
    const tNow = Date.now() / 1000;
    this.starMat.uniforms.uTime.value = tNow % 10000;
    // Features resolve only up close: fully washed out by 4.5R — planets
    // orbit beyond that, so from any world the sun is OUR sun: a disc of
    // pure light. Spots and granulation are a close-approach reward.
    this.starMat.uniforms.uFar.value = Math.max(
      0,
      Math.min(1, (dist / this.def.star.radius - 2) / 2.5),
    );
    // Small flares only resolve near the star; giants carry further.
    const starDist = dist / this.def.star.radius;
    const nearStar = Math.max(0, Math.min(1, (9 - starDist) / 6));
    const farVis = Math.max(0, Math.min(1, (40 - starDist) / 25));
    for (const f of this.flares) {
      // Eruption cycle: rise fast, hang, sink back beneath the surface.
      const cyc = Math.max(0, Math.sin(tNow * f.speed + f.phase) - 0.35) / 0.65;
      const vis = cyc * (f.giant ? farVis : nearStar);
      f.mat.opacity = vis * 0.85;
      f.mesh.visible = vis > 0.02;
      if (f.mesh.visible) {
        // The loop physically rises out of the photosphere and returns.
        const lift = f.base * (0.992 + cyc * (f.giant ? 0.035 : 0.012));
        f.mesh.position.copy(f.up).multiplyScalar(lift);
        const sc = 0.6 + cyc * 0.5;
        f.mesh.scale.set(sc, sc, sc);
      }
    }
    const departFade = this.departDir ? Math.max(0, 1 - this.departK * 1.4) : 1;
    // Up close the corona must thin out and shrink, or the sprite swallows
    // the photosphere — the whole point of having a surface.
    // A soft LIGHT around the star at every range — no rim-shell "orb".
    const starProx = Math.max(0, Math.min(1, (dist / this.def.star.radius - 2.4) / 4));
    // Sprite scale is WIDTH: the disc alone is 2R wide, so the halo needs
    // 4-5R to actually wrap it in light.
    const glowScale =
      Math.max(this.def.star.radius * 4.6, dist * 0.12) * this.bodyZoom * departFade;
    this.sunGlow.scale.set(glowScale, glowScale, 1);
    (this.sunGlow.material as THREE.SpriteMaterial).opacity = 0.3 + 0.7 * starProx;

    // Starfield + celestial paints are "at infinity": follow the camera.
    this.starfield.position.copy(shipPos).sub(origin);
    this.starStreaks.position.copy(this.starfield.position);
    this.celestials.position.copy(this.starfield.position);
    this.neighborGroup.position.copy(this.starfield.position);
    // Aimed-star highlight: quick eased fade in/out, breathing while held.
    for (const [idx, g] of this.aimGlow) {
      g.k += (g.target - g.k) * (1 - Math.exp(-14 * dt));
      const sp = this.neighborGroup.children[idx] as THREE.Sprite;
      const base = sp.userData.baseScale as number;
      const baseOp = (sp.userData.baseOpacity as number) ?? sp.material.opacity;
      const breathe = 0.45 + 0.22 * Math.sin(performance.now() * 0.004);
      sp.scale.set(base * (1 + g.k * breathe), base * (1 + g.k * breathe), 1);
      sp.material.opacity = baseOp + (1 - baseOp) * g.k;
      if (g.target === 0 && g.k < 0.02) {
        sp.scale.set(base, base, 1);
        sp.material.opacity = baseOp;
        this.aimGlow.delete(idx);
      }
    }

    this.idleFrames = this.pool.queueDepth === 0 ? this.idleFrames + 1 : 0;

    this.lodTimer -= dt;
    if (this.lodTimer <= 0) {
      this.lodTimer = 0.12;
      const local = new THREE.Vector3();
      for (const p of this.planets) {
        local.copy(shipPos).sub(p.pose.center).applyQuaternion(p.pose.invQuat);
        p.update(local, onFoot);
      }
    }
  }

  /**
   * Sky-driven ambient + hemisphere FILL — called from main once the sky colour is known
   * (after updateAtmosphere). Inside an atmosphere the fill takes the sky's own colour
   * (blue noon → warm golden hour); off-planet + on the night side it fades to a cool
   * MOONLIGHT/starlight. `sky` = the horizon sky colour; `presence` = 0 in open space → 1
   * deep in atmosphere. The intensities stay on the day/night curve (set in update()).
   */
  applySkyLighting(sky: THREE.Color, presence: number, cloudDepth = 0): number {
    const p = Math.max(0, Math.min(1, presence));
    // sky colour in atmosphere, cool moonlight in space
    this.skyFillTmp.lerpColors(this.moonLight, sky, p);
    coolAtmosphericFill(this.skyFillTmp,this.dayFactor,p);
    // The dark-side moonlight warms from deep blue (new moon) toward a brighter silver as the
    // lit moon climbs — so a full-moon night reads cooler-but-brighter, a new-moon one near-black.
    this.moonTint.copy(this.moonLight).lerp(MOON_FULL_TINT, this.moonGlow);
    // cool the fill toward that tint on the dark side (golden-hour warmth survives to dusk)
    this.ambient.color.copy(this.skyFillTmp).lerp(this.moonTint, (1 - this.dayFactor) * 0.7);
    this.hemi.color.copy(this.skyFillTmp);
    daylightGroundBounce(this.hemi.groundColor,this.groundPalette,this.dayFactor,p);
    // Reset by update() each frame: this cannot compound across render frames.
    const weather=weatherLightBalance(this.dayFactor,p,cloudDepth);
    this.sunLight.intensity *= weather.transmission;
    this.moonDirLight.intensity *= weather.transmission;
    this.ambient.intensity *= weather.ambient;
    this.hemi.intensity *= weather.hemisphere;
    return weather.transmission;
  }

  /**
   * Bake cloud fields. The volumetric march is expensive, so it runs OFFSCREEN here:
   * the planet the player is at re-bakes a few times a second (billow + sun tracking),
   * and distant air worlds refresh slowly — one per frame — so they show moving
   * clouds from space without a startup hitch. `time` is a small session-local clock.
   */
  bakeWeather(renderer: THREE.WebGLRenderer, shipPos: THREE.Vector3, time: number): void {
    const near = this.nearestPlanet(shipPos)?.planet ?? null;
    near?.bakeClouds(renderer, time, true);
    for (const p of this.planets) {
      if (p === near) continue;
      if (p.bakeClouds(renderer, time, false)) break; // one due distant world per frame
    }
  }

  dispose(): void {
    for (const p of this.planets) {
      this.scene.remove(p.group);
      p.dispose();
    }
    for (const o of this.sceneExtras) this.scene.remove(o);
    this.beltGroup.traverse((o) => {
      if (o instanceof THREE.InstancedMesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
        o.dispose();
      }
    });
    this.starBody.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
    this.neighborGroup.traverse((o) => {
      if (o instanceof THREE.Sprite) o.material.dispose();
    });
    this.celestials.traverse((o) => {
      if (o instanceof THREE.Sprite) o.material.dispose();
      if (o instanceof THREE.Points) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
    this.starfield.geometry.dispose();
    (this.starfield.material as THREE.Material).dispose();
    (this.sunGlow.material as THREE.SpriteMaterial).map?.dispose();
    this.sunGlow.material.dispose();
    this.pool.dispose();
  }
}

/**
 * The star itself: an enormous emissive body with a BOILING surface —
 * granulation cells from animated 3D value-noise, limb darkening toward
 * the edge — and a set of prominence arcs that rise off the limb and die
 * back down on their own clocks. Pure shader light: no scene lighting
 * touches it.
 */
/**
 * Asteroid belts: one InstancedMesh per belt — a seeded annulus of tumbling
 * rock filling the gap between two orbits. Positions are system-space
 * absolutes (the group rides the floating origin like the planets do).
 */
function makeBelts(def: SystemDef): THREE.Group {
  const group = new THREE.Group();
  for (const belt of def.belts) {
    const rng = rngFromString(belt.seed);
    // One seeded boulder for the whole belt, instanced hundreds of times.
    const geo = new THREE.IcosahedronGeometry(1, 1);
    const posA = geo.getAttribute("position");
    for (let i = 0; i < posA.count; i++) {
      const k = 1 + (rng() * 2 - 1) * 0.4;
      posA.setXYZ(i, posA.getX(i) * k, posA.getY(i) * k, posA.getZ(i) * k);
    }
    geo.computeVertexNormals();
    const mat = new THREE.MeshLambertMaterial({
      color: new THREE.Color().setHSL(0.07 + rng() * 0.06, 0.15 + rng() * 0.15, 0.32 + rng() * 0.12),
    });
    const inst = new THREE.InstancedMesh(geo, mat, belt.count);
    const n = new THREE.Vector3(...belt.normal);
    const t1 = new THREE.Vector3(1, 0, 0).cross(n);
    if (t1.lengthSq() < 1e-6) t1.set(0, 0, 1).cross(n);
    t1.normalize();
    const t2 = n.clone().cross(t1);
    const p = new THREE.Vector3();
    const q = new THREE.Quaternion();
    const sc = new THREE.Vector3();
    const m = new THREE.Matrix4();
    for (let i = 0; i < belt.count; i++) {
      const a = rng() * Math.PI * 2;
      // Triangular radial spread: dense mid-belt, feathered edges.
      const rr = belt.radius + (rng() + rng() - 1) * belt.width;
      const h = (rng() + rng() - 1) * belt.thickness;
      p.copy(t1).multiplyScalar(Math.cos(a) * rr)
        .addScaledVector(t2, Math.sin(a) * rr)
        .addScaledVector(n, h);
      q.setFromEuler(new THREE.Euler(rng() * Math.PI * 2, rng() * Math.PI * 2, rng() * Math.PI * 2));
      const s = 8 + Math.pow(rng(), 2.2) * 55; // many pebbles, few giants
      sc.set(s * (0.7 + rng() * 0.6), s * (0.7 + rng() * 0.6), s * (0.7 + rng() * 0.6));
      m.compose(p, q, sc);
      inst.setMatrixAt(i, m);
    }
    inst.instanceMatrix.needsUpdate = true;
    inst.frustumCulled = false; // annulus spans the system; just draw it
    group.add(inst);
  }
  return group;
}

interface FlareRec {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  speed: number;
  phase: number;
  up: THREE.Vector3;
  base: number;
  giant: boolean;
}

function makeStarBody(star: StarDef): {
  group: THREE.Group;
  mat: THREE.ShaderMaterial;
  flares: FlareRec[];
} {
  const group = new THREE.Group();
  const rng = rngFromString(star.seed);
  const c = new THREE.Color(star.color);
  const hot = c.clone().lerp(new THREE.Color(1, 1, 1), 0.55);
  const cool = c.clone().multiplyScalar(0.42);

  // Seeded surface character, the way planets roll terrain: activity sets
  // how violently the surface churns; sunspot groups land at seeded spots.
  const activity = 0.35 + rng() * 0.65;
  const cellScale = 7 + rng() * 6;
  const spotCount = 2 + Math.floor(rng() * 5);
  const spotDirs: THREE.Vector3[] = [];
  const spotRads: number[] = [];
  for (let i = 0; i < 7; i++) {
    let x = rng() * 2 - 1, y = (rng() * 2 - 1) * 0.7, z = rng() * 2 - 1;
    const l = Math.hypot(x, y, z) || 1;
    spotDirs.push(new THREE.Vector3(x / l, y / l, z / l));
    spotRads.push(0.05 + rng() * 0.13);
  }

  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uHot: { value: new THREE.Vector3(hot.r, hot.g, hot.b) },
      uCool: { value: new THREE.Vector3(cool.r, cool.g, cool.b) },
      uFar: { value: 0 },
      uAct: { value: activity },
      uCell: { value: cellScale },
      uSpots: { value: spotDirs },
      uSpotR: { value: spotRads },
      uSpotN: { value: spotCount },
    },
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      varying vec3 vN;
      varying vec3 vWp;
      void main() {
        vN = normalize(position);
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWp = wp.xyz;
        gl_Position = projectionMatrix * viewMatrix * wp;
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uHot;
      uniform vec3 uCool;
      uniform float uFar;
      uniform float uAct;
      uniform float uCell;
      uniform vec3 uSpots[7];
      uniform float uSpotR[7];
      uniform int uSpotN;
      #include <common>
      #include <logdepthbuf_pars_fragment>
      varying vec3 vN;
      varying vec3 vWp;
      float hash(vec3 p) {
        p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
        p *= 17.0;
        return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
      }
      float noise3(vec3 x) {
        vec3 i = floor(x);
        vec3 f = fract(x);
        f = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(mix(hash(i), hash(i + vec3(1, 0, 0)), f.x),
              mix(hash(i + vec3(0, 1, 0)), hash(i + vec3(1, 1, 0)), f.x), f.y),
          mix(mix(hash(i + vec3(0, 0, 1)), hash(i + vec3(1, 0, 1)), f.x),
              mix(hash(i + vec3(0, 1, 1)), hash(i + vec3(1, 1, 1)), f.x), f.y),
          f.z);
      }
      float fbm(vec3 p) {
        float s = 0.0, a = 0.5;
        for (int o = 0; o < 5; o++) {
          s += a * noise3(p);
          p *= 2.13;
          a *= 0.5;
        }
        return s;
      }
      void main() {
        #include <logdepthbuf_fragment>
        vec3 n = normalize(vN);
        // Granulation: convection cells crawling with the activity level.
        vec3 p = n * uCell;
        float drift = uTime * (0.03 + uAct * 0.05);
        float g1 = fbm(p + vec3(drift, drift * 0.6, -drift * 0.8));
        float g2 = fbm(p * 2.7 - vec3(drift * 1.4));
        float g = clamp(g1 * 0.75 + g2 * 0.45 * uAct + 0.18, 0.0, 1.2);
        vec3 col = mix(uCool, uHot, pow(g, 1.3)) * (1.05 + uAct * 0.25);
        // Sunspot groups: dark umbra, bright faculae ring around it.
        for (int i = 0; i < 7; i++) {
          if (i >= uSpotN) break;
          float d = acos(clamp(dot(n, uSpots[i]), -1.0, 1.0));
          float r = uSpotR[i];
          float umbra = 1.0 - smoothstep(r * 0.35, r * 0.8, d);
          float ring = smoothstep(r * 0.6, r * 0.95, d) * (1.0 - smoothstep(r * 0.95, r * 1.5, d));
          col *= 1.0 - umbra * 0.72;
          col *= 1.0 + ring * 0.3;
        }
        // Limb darkening: the edge cools and dims like a real photosphere.
        // The star group never rotates, so the object normal IS the world
        // normal (modelMatrix is vertex-only in GLSL anyway).
        vec3 view = normalize(cameraPosition - vWp);
        float mu = clamp(dot(n, view), 0.0, 1.0);
        float limb = 0.42 + 0.58 * pow(mu, 0.55);
        col *= mix(limb, 0.9 + 0.1 * limb, uFar);
        // From planet distance the photosphere is a featureless blinding
        // disc — granulation and spots wash out into pure radiance.
        col = mix(col, uHot * 1.3, uFar);
        gl_FragColor = vec4(col, 1.0);
      }
    `,
    fog: false,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(star.radius, 96, 64), mat);
  mesh.frustumCulled = false; // colossal sphere + floating origin: just draw it
  group.add(mesh);

  // Prominences: small loops hugging the photosphere, each SPURTING up
  // and settling back on a quick cycle. One or two giants per star are
  // visible from afar; the small ones only resolve up close.
  const flares: FlareRec[] = [];
  const COUNT = 14;
  for (let i = 0; i < COUNT; i++) {
    const giant = i < 2;
    const loopR = star.radius * (giant ? 0.06 + rng() * 0.05 : 0.014 + rng() * 0.022);
    const geo = new THREE.TorusGeometry(loopR, loopR * 0.16, 8, 24, 1.4 + rng() * 1.5);
    const fmat = new THREE.MeshBasicMaterial({
      color: hot,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      fog: false,
    });
    const flare = new THREE.Mesh(geo, fmat);
    let x = rng() * 2 - 1, y = rng() * 2 - 1, z = rng() * 2 - 1;
    const l = Math.hypot(x, y, z) || 1;
    const up = new THREE.Vector3(x / l, y / l, z / l);
    flare.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
    flare.rotateY(rng() * Math.PI * 2);
    group.add(flare);
    flares.push({
      mesh: flare,
      mat: fmat,
      speed: giant ? 0.08 + rng() * 0.06 : 0.35 + rng() * 0.5,
      phase: rng() * Math.PI * 2,
      up,
      base: star.radius,
      giant,
    });
  }
  return { group, mat, flares };
}

function makeSunSprite(color: THREE.Color): THREE.Sprite {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  // Layered gradient: white-hot core, saturated bloom, then a LONG soft
  // tail — the glow breathes outward instead of stopping at a ring.
  const rgba = (k: number, a: number): string =>
    `rgba(${Math.min(255, (color.r * 255 * k + 255 * Math.max(0, k - 1)) | 0)},${Math.min(255, (color.g * 255 * k) | 0)},${Math.min(255, (color.b * 255 * k) | 0)},${a})`;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.08, "rgba(255,255,255,0.95)");
  grad.addColorStop(0.16, rgba(1.25, 0.85));
  grad.addColorStop(0.28, rgba(1.05, 0.5));
  grad.addColorStop(0.45, rgba(0.95, 0.24));
  grad.addColorStop(0.68, rgba(0.85, 0.09));
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: new THREE.CanvasTexture(canvas),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    }),
  );
  return sprite;
}

function makeStarfield(seed: string): THREE.Points {
  const rng = rngFromString(`${seed}:stars`);
  const count = 6000;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const R = 800000;
  for (let i = 0; i < count; i++) {
    // Uniform direction via normalized gaussian-ish triple.
    let x = rng() * 2 - 1, y = rng() * 2 - 1, z = rng() * 2 - 1;
    const l = Math.sqrt(x * x + y * y + z * z) || 1;
    positions[i * 3] = (x / l) * R;
    positions[i * 3 + 1] = (y / l) * R;
    positions[i * 3 + 2] = (z / l) * R;
    const warm = rng();
    const bright = 0.4 + rng() * 0.6;
    colors[i * 3] = bright * (0.8 + warm * 0.2);
    colors[i * 3 + 1] = bright * 0.9;
    colors[i * 3 + 2] = bright * (1.0 - warm * 0.15);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const mat = new THREE.PointsMaterial({
    size: 2.0,
    sizeAttenuation: false,
    vertexColors: true,
    fog: false,
    depthWrite: false,
    transparent: true, // crossfades with the streak lines during hyperlight
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  points.renderOrder = -1;
  return points;
}

/**
 * The painted sky: nebulae and a galactic band, anchored in UNIVERSE space.
 *
 * Features have fixed positions in galaxy-lattice coordinates (systems sit
 * at integer coords). Each system computes the DIRECTION to every feature
 * from its own coordinate, so neighboring galaxies look at the same nebula
 * from different sides — fly ten systems spinward and the band sits
 * differently on the sky. One global seed; zero per-system storage.
 */
function makeCelestials(
  gx: number,
  gy: number,
  gz: number,
  outDirs: [number, number, number][],
): THREE.Group {
  const rng = rngFromString("hyperlight:celestials");
  const group = new THREE.Group();
  const R = 760_000; // just inside the starfield shell
  const sys = new THREE.Vector3(gx, gy, gz);
  const tex = makeNebulaTexture();

  // The galactic plane: a fixed universe direction.
  let nx = rng() * 2 - 1, ny = rng() * 2 - 1, nz = rng() * 2 - 1;
  const nl = Math.hypot(nx, ny, nz) || 1;
  const plane = new THREE.Vector3(nx / nl, ny / nl, nz / nl);

  const NEBULAE = 64;
  const HUES = [0.62, 0.7, 0.55, 0.78, 0.04, 0.52];
  const dir = new THREE.Vector3();
  const addSprite = (
    pos: THREE.Vector3,
    hue: number,
    sat: number,
    lit: number,
    opacity: number,
    size: number,
    squash: number,
  ): void => {
    dir.copy(pos).sub(sys).normalize();
    if (outDirs.length < 4) outDirs.push([dir.x, dir.y, dir.z]);
    const mat = new THREE.SpriteMaterial({
      map: tex,
      color: new THREE.Color().setHSL(hue, sat, lit),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity,
      fog: false,
      rotation: rng() * Math.PI * 2,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.position.copy(dir).multiplyScalar(R);
    sprite.scale.set(size, size * squash, 1);
    group.add(sprite);
  };
  for (let i = 0; i < NEBULAE; i++) {
    let px = rng() * 2 - 1, py = rng() * 2 - 1, pz = rng() * 2 - 1;
    const pl = Math.hypot(px, py, pz) || 1;
    const pos = new THREE.Vector3(px / pl, py / pl, pz / pl).multiplyScalar(8 + rng() * 22);
    // Half the features crowd the galactic plane (the band); the rest are
    // scattered bodies anywhere on the sky.
    if (rng() > 0.5) pos.addScaledVector(plane, -pos.dot(plane) * (0.72 + rng() * 0.24));
    const hue = (HUES[Math.floor(rng() * HUES.length)] + rng() * 0.05) % 1;
    addSprite(
      pos,
      hue,
      0.35 + rng() * 0.35,
      0.5 + rng() * 0.2,
      0.06 + rng() * 0.12,
      (0.3 + rng() * 0.85) * R * 0.5,
      0.55 + rng() * 0.45,
    );
  }
  // A handful of bright NEIGHBOR GALAXIES: small, sharply elliptical,
  // unmistakably bodies rather than haze.
  for (let i = 0; i < 5; i++) {
    let px = rng() * 2 - 1, py = rng() * 2 - 1, pz = rng() * 2 - 1;
    const pl = Math.hypot(px, py, pz) || 1;
    const pos = new THREE.Vector3(px / pl, py / pl, pz / pl).multiplyScalar(14 + rng() * 20);
    addSprite(
      pos,
      (0.55 + rng() * 0.2) % 1,
      0.15 + rng() * 0.2,
      0.75,
      0.16 + rng() * 0.1,
      (0.08 + rng() * 0.1) * R * 0.5,
      0.3 + rng() * 0.25,
    );
  }

  // Dense faint stars along the plane: the Milky-Way dust of the band.
  const BAND = 2600;
  const positions = new Float32Array(BAND * 3);
  const colors = new Float32Array(BAND * 3);
  const t1 = new THREE.Vector3(1, 0, 0).cross(plane);
  if (t1.lengthSq() < 0.01) t1.set(0, 1, 0).cross(plane);
  t1.normalize();
  const t2 = plane.clone().cross(t1);
  const p = new THREE.Vector3();
  for (let i = 0; i < BAND; i++) {
    const ang = rng() * Math.PI * 2;
    const rad = 10 + rng() * 35;
    const thick = (rng() + rng() - 1) * 2.2; // triangular: dense mid-plane
    p.copy(t1).multiplyScalar(Math.cos(ang) * rad)
      .addScaledVector(t2, Math.sin(ang) * rad)
      .addScaledVector(plane, thick)
      .sub(sys)
      .normalize()
      .multiplyScalar(R);
    positions[i * 3] = p.x;
    positions[i * 3 + 1] = p.y;
    positions[i * 3 + 2] = p.z;
    const warm = rng();
    const dim = 0.12 + rng() * 0.3; // fainter than the main stars
    colors[i * 3] = dim * (0.75 + warm * 0.25);
    colors[i * 3 + 1] = dim * 0.85;
    colors[i * 3 + 2] = dim;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const bandPts = new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      size: 1.5,
      sizeAttenuation: false,
      vertexColors: true,
      fog: false,
      depthWrite: false,
      transparent: true,
      blending: THREE.AdditiveBlending,
    }),
  );
  bandPts.frustumCulled = false;
  bandPts.renderOrder = -1;
  group.add(bandPts);

  // --- Glow strips: broad luminous washes along the band's spine ---
  for (let i = 0; i < 8; i++) {
    const ang = rng() * Math.PI * 2;
    const rad = 14 + rng() * 26;
    p.copy(t1).multiplyScalar(Math.cos(ang) * rad)
      .addScaledVector(t2, Math.sin(ang) * rad)
      .addScaledVector(plane, (rng() - 0.5) * 1.2)
      .sub(sys);
    const gdir = p.clone().normalize();
    const mat = new THREE.SpriteMaterial({
      map: tex,
      color: new THREE.Color().setHSL(0.58 + rng() * 0.1, 0.25, 0.6),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity: 0.035 + rng() * 0.03,
      fog: false,
      rotation: rng() * Math.PI * 2,
    });
    const strip = new THREE.Sprite(mat);
    strip.position.copy(gdir).multiplyScalar(R);
    const w = (0.7 + rng() * 0.6) * R * 0.5;
    strip.scale.set(w, w * 0.28, 1);
    group.add(strip);
  }

  // --- Dust lanes: DARK clouds over the bright band (what makes a milky
  // way read as one — the silhouette, not just the glow) ---
  for (let i = 0; i < 12; i++) {
    const ang = rng() * Math.PI * 2;
    const rad = 12 + rng() * 28;
    p.copy(t1).multiplyScalar(Math.cos(ang) * rad)
      .addScaledVector(t2, Math.sin(ang) * rad)
      .addScaledVector(plane, (rng() - 0.5) * 1.6)
      .sub(sys);
    const ddir = p.clone().normalize();
    const mat = new THREE.SpriteMaterial({
      map: tex,
      color: new THREE.Color(0x01010a),
      blending: THREE.NormalBlending,
      depthWrite: false,
      transparent: true,
      opacity: 0.3 + rng() * 0.25,
      fog: false,
      rotation: rng() * Math.PI * 2,
    });
    const dust = new THREE.Sprite(mat);
    dust.position.copy(ddir).multiplyScalar(R * 0.985); // just inside the glow
    const w = (0.18 + rng() * 0.4) * R * 0.5;
    dust.scale.set(w, w * (0.3 + rng() * 0.3), 1);
    dust.renderOrder = 1; // over the band and nebulae
    group.add(dust);
  }

  // --- Bright feature stars: three size tiers with real color temperature
  // (blue-white giants to deep orange), half seeded into the band ---
  const spike = makeStarSpikeTexture();
  for (const [count, px, baseDim] of [[150, 4, 0.5], [70, 7, 0.7], [26, 11, 0.9]] as const) {
    const bpos = new Float32Array(count * 3);
    const bcol = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      let x = rng() * 2 - 1, y = rng() * 2 - 1, z = rng() * 2 - 1;
      const l = Math.hypot(x, y, z) || 1;
      p.set(x / l, y / l, z / l).multiplyScalar(20 + rng() * 30);
      if (rng() > 0.5) p.addScaledVector(plane, -p.dot(plane) * (0.7 + rng() * 0.25));
      p.sub(sys).normalize().multiplyScalar(R * 1.01);
      bpos[i * 3] = p.x;
      bpos[i * 3 + 1] = p.y;
      bpos[i * 3 + 2] = p.z;
      // Color temperature: t=0 deep orange → t=1 blue-white.
      const temp = rng();
      const bright = baseDim * (0.7 + rng() * 0.3);
      bcol[i * 3] = bright * (1 - temp * 0.35);
      bcol[i * 3 + 1] = bright * (0.75 + temp * 0.2);
      bcol[i * 3 + 2] = bright * (0.55 + temp * 0.45);
    }
    const bgeo = new THREE.BufferGeometry();
    bgeo.setAttribute("position", new THREE.BufferAttribute(bpos, 3));
    bgeo.setAttribute("color", new THREE.BufferAttribute(bcol, 3));
    const pts = new THREE.Points(
      bgeo,
      new THREE.PointsMaterial({
        map: spike,
        size: px,
        sizeAttenuation: false,
        vertexColors: true,
        fog: false,
        depthWrite: false,
        transparent: true,
        blending: THREE.AdditiveBlending,
      }),
    );
    pts.frustumCulled = false;
    pts.renderOrder = 2;
    group.add(pts);
  }

  group.renderOrder = -2; // paints sit behind the crisp stars
  return group;
}

/**
 * Neighboring systems (lattice distance <= 3) as bright four-point stars at
 * their true directions. The galactic chart's coordinates, hung on the sky:
 * aim the nose at one and jump to it.
 */
function makeNeighborStars(
  gx: number,
  gy: number,
  gz: number,
  out: NeighborStar[],
): THREE.Group {
  const group = new THREE.Group();
  const tex = makeStarSpikeTexture();
  const R = 700_000;
  const dir = new THREE.Vector3();
  for (let dx = -3; dx <= 3; dx++) {
    for (let dy = -3; dy <= 3; dy++) {
      for (let dz = -3; dz <= 3; dz++) {
        const d = Math.hypot(dx, dy, dz);
        if (d === 0 || d > 3) continue;
        const sx = gx + dx, sy = gy + dy, sz = gz + dz;
        const id = systemIdAt(sx, sy, sz);
        const ref = parseSystemRef(id);
        const sys = ref?.kind === "sealed"
          ? (systemDefFor(id) ?? sealedSystemStub(sx, sy, sz))
          : generateSystem(sx, sy, sz);
        dir.set(dx, dy, dz).divideScalar(d);
        out.push({
          id,
          sx,
          sy,
          sz,
          name: sys.name,
          dist: d,
          dir: [dir.x, dir.y, dir.z],
        });
        const mat = new THREE.SpriteMaterial({
          map: tex,
          color: new THREE.Color(sys.star.color),
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          transparent: true,
          opacity: Math.min(1, 1.7 / d),
          fog: false,
        });
        const sprite = new THREE.Sprite(mat);
        sprite.position.copy(dir).multiplyScalar(R);
        // Use the PRE-scale star radius so neighbour-system markers keep their
        // original on-screen size (the backdrop sphere R didn't scale either).
        const size = (15000 + (sys.star.radius / WORLD_SCALE) * 0.8) / d;
        sprite.scale.set(size, size, 1);
        sprite.renderOrder = 3; // above the painted sky
        sprite.userData.baseScale = size;
        group.add(sprite);
      }
    }
  }
  return group;
}

/** Four-point star glow shared by all neighbor-system sprites. */
let starSpikeTex: THREE.CanvasTexture | null = null;
function makeStarSpikeTexture(): THREE.CanvasTexture {
  if (starSpikeTex) return starSpikeTex;
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const c = size / 2;
  const core = ctx.createRadialGradient(c, c, 0, c, c, size * 0.16);
  core.addColorStop(0, "rgba(255,255,255,1)");
  core.addColorStop(0.5, "rgba(255,255,255,0.5)");
  core.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = core;
  ctx.fillRect(0, 0, size, size);
  // Diffraction spikes.
  for (const [wx, wy] of [[1, 0], [0, 1]] as const) {
    const g = ctx.createLinearGradient(c - wx * c, c - wy * c, c + wx * c, c + wy * c);
    g.addColorStop(0, "rgba(255,255,255,0)");
    g.addColorStop(0.5, "rgba(255,255,255,0.55)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    if (wx) ctx.fillRect(0, c - 1.2, size, 2.4);
    else ctx.fillRect(c - 1.2, 0, 2.4, size);
  }
  starSpikeTex = new THREE.CanvasTexture(canvas);
  return starSpikeTex;
}

/** Soft radial cloud texture shared by all nebula sprites. */
let nebulaTex: THREE.CanvasTexture | null = null;
function makeNebulaTexture(): THREE.CanvasTexture {
  if (nebulaTex) return nebulaTex;
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  // Layered offset blobs read as wisps rather than a perfect disc.
  for (let i = 0; i < 7; i++) {
    const cx = size / 2 + (Math.sin(i * 2.4) * size) / 7;
    const cy = size / 2 + (Math.cos(i * 1.7) * size) / 7;
    const r = size * (0.18 + 0.05 * i);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, "rgba(255,255,255,0.16)");
    g.addColorStop(0.55, "rgba(255,255,255,0.05)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  nebulaTex = new THREE.CanvasTexture(canvas);
  return nebulaTex;
}

// Streak state lives at module scope so it survives the StarSystem swap at
// hyperlight arrival — the streaking sky is pixel-continuous across systems.
let streakPhase = 0;

/** Line-segment twin of the starfield, for the hyperlight red-shift streaks. */
function makeStarStreaks(starfield: THREE.Points): THREE.LineSegments {
  const base = starfield.geometry.getAttribute("position").array as Float32Array;
  const baseCol = starfield.geometry.getAttribute("color").array as Float32Array;
  const n = base.length / 3;
  const positions = new Float32Array(n * 2 * 3);
  const colors = new Float32Array(n * 2 * 3);
  for (let i = 0; i < n; i++) {
    // Head keeps the star color; tail fades out.
    colors[i * 6] = baseCol[i * 3];
    colors[i * 6 + 1] = baseCol[i * 3 + 1];
    colors[i * 6 + 2] = baseCol[i * 3 + 2];
    colors[i * 6 + 3] = baseCol[i * 3] * 0.1;
    colors[i * 6 + 4] = baseCol[i * 3 + 1] * 0.1;
    colors[i * 6 + 5] = baseCol[i * 3 + 2] * 0.1;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const lines = new THREE.LineSegments(
    geo,
    new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    }),
  );
  // Per-star streak variation: length jitter (uniform lengths read as a
  // hard angular "ring" where streaking kicks in), plus a sweep phase and
  // rate so each streak cycles through its star on its own beat.
  const rng = rngFromString("hyperlight:streak-jitter");
  const jitter = new Float32Array(n);
  const phase = new Float32Array(n);
  const rate = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    jitter[i] = 0.45 + rng() * 0.9;
    phase[i] = rng();
    rate[i] = 0.7 + rng() * 0.6;
  }
  lines.userData.lenJitter = jitter;
  lines.userData.sweepPhase = phase;
  lines.userData.sweepRate = rate;
  lines.visible = false;
  lines.frustumCulled = false;
  lines.renderOrder = -1;
  return lines;
}
